import {Component, inject, Inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatIcon} from '@angular/material/icon';
import {CdkCopyToClipboard} from '@angular/cdk/clipboard';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MapService} from '../api/services/map.service';
import {SelectInputComponent} from '../shared/forms/select-input/select-input.component';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {TextInputComponent} from '../shared/forms/text-input/text-input.component';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {LinkViewMode} from '../api/services/setting.service';
import {Observable, catchError, concat, forkJoin, of} from 'rxjs';
import {GraphMap} from '../api/interfaces/graph-node.interface';
import {SlideToggleInputComponent} from '../shared/forms/slide-select/slide-toggle-input.component';
import {
  MaporiumAccordionItemComponent
} from '../shared/components/maporium-accordion/maporium-accordion-item.component';
import {CdkAccordion} from '@angular/cdk/accordion';
import {HasPackageDirective} from '../packages/has-package.directive';
import {
  isSmapletComments,
  OtherLinksMode,
  OtherNodesMode,
  PACKAGE_NAMES,
  SmapletComments,
  smapletCommentsOf
} from '@maporium-workspace/shared';
import {VisibilityWarningDialogComponent} from './visibility-warning.dialog';
import {StoreService} from '../api/services/store.service';
import {TourResource} from '../api/resource/tour.resource';
import {ViewResource} from '../api/resource/view.resource';
import {MapTour} from '../api/interfaces/map-tour';
import {SavedView} from '../api/interfaces/saved-view';
import {UserResource} from '../api/resource/user.resource';

// Mirrors SMAPLET_PATH in apps/maporium-v2/src/app/routes/embed/embed-params.ts.
const SMAPLET_PATH = '/dashboard/smaplet';

/**
 * a smaplet offers three linkage presets. Older embeds are read from
 * their link view alone: selection-attached or -enclosed both mean "connected".
 */
function linkagePresetOf(mode: LinkViewMode): LinkViewMode {
  if (mode === LinkViewMode.ALL || mode === LinkViewMode.OFF) return mode;
  return LinkViewMode.RELATED;
}

const NO_OPENING = 'none';

function openingKeyOf(storyId?: string | null, sceneId?: string | null): string {
  if (storyId) return `story:${storyId}`;
  if (sceneId) return `scene:${sceneId}`;
  return NO_OPENING;
}

function byName<T extends {name: string}>(a: T, b: T): number {
  return (a.name ?? '').localeCompare(b.name ?? '', undefined, {sensitivity: 'base'});
}

const LINKAGE_PRESET_MODES: Record<number, {otherLinksMode: OtherLinksMode; otherNodesMode: OtherNodesMode}> = {
  [LinkViewMode.ALL]: {otherLinksMode: OtherLinksMode.DIMMED, otherNodesMode: OtherNodesMode.SHOWN},
  [LinkViewMode.RELATED]: {otherLinksMode: OtherLinksMode.HIDDEN, otherNodesMode: OtherNodesMode.SHOWN},
  [LinkViewMode.OFF]: {otherLinksMode: OtherLinksMode.HIDDEN, otherNodesMode: OtherNodesMode.SHOWN},
};

@Component({
  selector: 'app-embed-dialog',
  styles: [`
    ::ng-deep {

      .share-map-form {
        --mdc-theme-primary: #000000;

        .mdc-floating-label {
          color: #000000 !important;
        }
      }

      #buttonName {
        input {
          color: rgba(0, 0, 0, 0.6);
          font-size: 16px;
          padding-left: 25px;
        }
      }


    }
  `],
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <span class="material-symbols-rounded me-2">integration_instructions</span>
        {{ 'EMBED_DIALOG.TITLE' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" mat-dialog-close
            matTooltip="{{ 'SHARE_DIALOG.CLOSE_TOOLTIP' | translate }}"></span>
    </div>
    <div mat-dialog-content class="p-3 px-4">
      <cdk-accordion [multi]="true" class="accordion">
        <maporium-accordion-item
          [title]="'EMBED_DIALOG.ACCORDION.ACCESS' | translate">
          <div style="background-color: #e7f1fa; border-radius: 8px; padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; gap: 8px;">
            <span style="color: #3d85c6; font-size: 14px; word-break: break-all;">{{ smapletUrl() }}</span>
            <button mat-icon-button
                    [cdkCopyToClipboard]="smapletUrl()"
                    matTooltip="{{ 'EMBED_DIALOG.SMAPLET_URL.COPY_TO_CLIPBOARD' | translate }}"
                    (click)="onUrlCopied()"
                    style="flex-shrink: 0; color: #3d85c6;">
              <mat-icon fontSet="material-symbols-rounded" style="color: #3d85c6;">content_copy</mat-icon>
            </button>
          </div>

          <app-select-input
            [label]="'EMBED_DIALOG.ACCESS.LEVEL' | translate"
            [formControl]="accessControl"
            [options]="accessOptions"
            (selectionChange)="onAccessChange($event)">
          </app-select-input>

          <app-slide-toggle-input [formControl]="allowWebsiteEmbeddingControl"
                                  [label]="'EMBED_DIALOG.ALLOW_WEBSITE_EMBEDDING' | translate"
                                  (click)="onEmbeddingToggleClick()"></app-slide-toggle-input>

          <app-select-input [formControl]="commentsControl"
                            *appHasPackage="[packagenames.analyst, packagenames.publisher, packagenames.developer]"
                            data-testid="embed-comments"
                            [label]="'EMBED_DIALOG.VISITOR_COMMENTS' | translate"
                            [isMaporiumIcon]="true"
                            [options]="commentsOptions"></app-select-input>
        </maporium-accordion-item>

        <maporium-accordion-item
          [title]="'EMBED_DIALOG.ACCORDION.CONFIGURATION' | translate">
          <app-select-input
            [label]="'EMBED_DIALOG.SM_SETUP.LINK_DISPLAY_MODE' | translate"
            [formControl]="linkViewModeControl"
            [isMaporiumIcon]="true"
            [options]="linkViewModeOptions">
          </app-select-input>
          <app-select-input
            [label]="'EMBED_DIALOG.OPENING.LABEL' | translate"
            [formControl]="openingControl"
            [isMaporiumIcon]="true"
            [options]="openingOptions"
            (selectionChange)="onOpeningChange($event.key)">
          </app-select-input>
          @if (openingIsScene()) {
            <app-slide-toggle-input [formControl]="silentControl"
                                    [label]="'EMBED_DIALOG.OPENING.SILENT' | translate"></app-slide-toggle-input>
          }
          @if (smaplet3dAllowed) {
            <div [matTooltip]="'EMBED_DIALOG.VIEW.TOOLTIP' | translate" data-testid="embed-view">
              <app-select-input
                [label]="'EMBED_DIALOG.VIEW.LABEL' | translate"
                [formControl]="viewControl"
                [options]="viewOptions">
              </app-select-input>
            </div>
            @if (viewControl.value === '3d') {
              <div [matTooltip]="'EMBED_DIALOG.AUTO_ORBIT.TOOLTIP' | translate" data-testid="embed-auto-orbit">
                <app-slide-toggle-input [formControl]="autoOrbitControl"
                                        [label]="'EMBED_DIALOG.AUTO_ORBIT.LABEL' | translate"></app-slide-toggle-input>
              </div>
            }
          }
          <app-select-input
            [label]="'EMBED_DIALOG.STYLE.CHOOSE' | translate"
            [formControl]="embedOption"
            [options]="embedOptions">
          </app-select-input>

          @if (embedOption.value === 'button' || embedOption.value === 'link') {
            <div class="mt-3">
              <app-text-input id="buttonName"
                              [autoFocusAndSelect]="true"
                              [label]="getDynamicLabelKey() | translate"
                              [title]="getDynamicLabelKey() | translate"
                              [formControl]="buttonOrLinkNameControl"></app-text-input>
            </div>
          }
        </maporium-accordion-item>

        <maporium-accordion-item
          [title]="'EMBED_DIALOG.ACCORDION.EMBED_CODE' | translate">
          <div
              style="background-color: #e7f1fa; border-radius: 8px; padding: 12px 16px; display: flex; align-items: flex-start; justify-content: space-between; gap: 8px;">
            <pre
                style="color: #3d85c6; font-size: 13px; white-space: pre-wrap; word-break: break-all; margin: 0; font-family: monospace; flex: 1;">{{ embedSnippet() }}</pre>
            <button
              mat-icon-button
              [cdkCopyToClipboard]="embedSnippet()"
              [matTooltip]="'EMBED_DIALOG.SNIPPET.COPY_TO_CLIPBOARD' | translate"
              (click)="onCodeCopied()"
              style="flex-shrink: 0; color: #3d85c6;">
              <mat-icon class="material-symbols-outlined" style="color: #3d85c6;">content_copy</mat-icon>
            </button>
          </div>
        </maporium-accordion-item>
      </cdk-accordion>
    </div>

    <div mat-dialog-actions class="d-flex justify-content-evenly">
      <button class="dialog-action-btn dialog-action-btn-cancel" mat-dialog-close>{{ 'EMBED_DIALOG.ACTIONS.CANCEL' | translate }}</button>
      <button class="dialog-action-btn dialog-action-btn-primary" [disabled]="!isInitialized || saving" (click)="done()">{{ 'EMBED_DIALOG.ACTIONS.UPDATE' | translate }}</button>
    </div>
  `,
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatTooltipModule,
    MatIcon,
    CdkCopyToClipboard,
    SelectInputComponent,
    ReactiveFormsModule,
    TextInputComponent,
    TranslatePipe,
    SlideToggleInputComponent,
    MaporiumAccordionItemComponent,
    CdkAccordion,
    HasPackageDirective
  ]
})

export class EmbedDialogComponent {
  snackBar = inject(MatSnackBar);

  accessControl = new FormControl('private');
  allowWebsiteEmbeddingControl = new FormControl(false);
  commentsControl = new FormControl<string>('none');
  commentsOptions: { key: SmapletComments; value: string; icon: string; isSeparatorAfter?: boolean }[] = [];

  private initialAllowEmbedding = false;
  private initialIsPublished = false;
  isInitialized = false;
  saving = false;
  /**
   * What the smap held when the dialog opened. Update sends only what differs
   * from this: the selects reach their form control through a 200ms debounce
   * (BaseCustomInputComponent), so writing all five fields every time let a
   * control that had not caught up overwrite a setting nobody touched.
   */
  private saved: {
    isPublic: boolean;
    comments: SmapletComments;
    allowEmbedding: boolean;
    linkViewMode: number;
    otherLinksMode: OtherLinksMode;
    otherNodesMode: OtherNodesMode;
    openingKey: string;
    openingSilent: boolean;
    view: '2d' | '3d';
    autoOrbit: boolean;
  } | null = null;

  embedOption = new FormControl<'iframe' | 'button' | 'link'>('iframe');
  embedOptions: { key: string; value: string; icon: string }[] = [];
  accessOptions: { key: string; value: string; icon: string }[] = [];
  // a smaplet offers three presets; "Show connected links" by default.
  linkViewModeControl = new FormControl(LinkViewMode.ALL.toString());
  linkViewModeOptions: { key: string; value: string; icon: string }[] = [];

  buttonOrLinkNameControl = new FormControl<string>(''); // Control for customizing name

  smaplet3dAllowed = false;
  viewControl = new FormControl<string>('2d');
  autoOrbitControl = new FormControl(false);
  viewOptions: { key: string; value: string; icon: string }[] = [];

  openingControl = new FormControl<string>(NO_OPENING);
  silentControl = new FormControl(true);
  openingOptions: { key: string; value: string; icon: string; isSeparatorAfter?: boolean }[] = [];
  private hiddenSceneIds = new Set<string>();

  private host = location.origin;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      map: GraphMap;
      mapName: string;
      isPublic: boolean
    },
    private matDialogRef: MatDialogRef<EmbedDialogComponent>,
    private dialog: MatDialog,
    private mapService: MapService,
    private translate: TranslateService,
    private storeService: StoreService,
    private tourResource: TourResource,
    private viewResource: ViewResource,
    private userResource: UserResource
  ) {
    // Initialize options first (sync translations if already cached, or wait)
    this.initializeOptions();
    // The dialog renders before the smap arrives. Leaving the controls live in
    // that window let a quick change be overwritten by the load a moment
    // later, which is why saving looked unreliable rather than broken
    // (MAP-1682). Nothing is editable until the stored values are in.
    this.gatedControls().forEach((control) =>
      control.disable({ emitEvent: false })
    );

    // One load, one place that fills the form, one flag. Nested subscribes left
    // half the form populated from one response and half from another, so
    // whichever landed after the user touched a control overwrote it, and a
    // failing store check left the dialog looking loaded while still holding
    // its defaults - which Update then wrote back over the real settings.
    forkJoin({
      map: this.mapService.findByIdWithRelation(data.map.id),
      published: this.storeService
        .isPublished(data.map.id)
        .pipe(catchError(() => of({ isPublished: false }))),
      tours: this.tourResource.getAll(data.map.id).pipe(catchError(() => of([] as MapTour[]))),
      views: this.viewResource.getAll(data.map.id).pipe(catchError(() => of([] as SavedView[]))),
      features: this.userResource.getFeatures(data.map.id).pipe(catchError(() => of({ canvas3d: false, smaplet3d: false }))),
    }).subscribe({
      next: ({ map, published, tours, views, features }) => {
        this.smaplet3dAllowed = !!features?.smaplet3d;
        this.buildOpeningOptions(tours ?? [], views ?? []);
        this.loaded(map, published.isPublished);
      },
      error: () => this.loadFailed(),
    });

    // Guest comments used to write the moment the control changed. The toggle's
    // inner control echoes a value on init, the base input debounces that into
    // its onChange, and the write went out on open with nobody having touched
    // it - taking `isPublic` with it, since the endpoint forces a smap public
    // when guest comments go on. It is staged with everything else now and
    // committed by Update (MAP-1682).
  }

  /** Every control takes its stored value here, in one pass, before Update unlocks. */
  private loaded(map: GraphMap, isPublished: boolean): void {
    this.initialAllowEmbedding = map.allowWebsiteEmbedding ?? false;
    this.initialIsPublished = isPublished;
    const savedOpeningKey = openingKeyOf(map.smapletOpeningStoryId, map.smapletOpeningSceneId);
    this.saved = {
      isPublic: !!map.isPublic,
      comments: smapletCommentsOf(map),
      allowEmbedding: map.allowWebsiteEmbedding ?? false,
      linkViewMode: map.smapletLinkViewMode ?? LinkViewMode.ALL,
      otherLinksMode: map.smapletOtherLinksMode ?? OtherLinksMode.HIDDEN,
      otherNodesMode: map.smapletOtherNodesMode ?? OtherNodesMode.SHOWN,
      openingKey: savedOpeningKey,
      openingSilent: map.smapletOpeningSilent ?? true,
      view: map.smapletView === '3d' ? '3d' : '2d',
      autoOrbit: map.smapletAutoOrbit === true,
    };
    // A story or scene that is gone, or a story now hidden, opens on nothing.
    const openingKey = this.openingOptions.some((o) => o.key === savedOpeningKey)
      ? savedOpeningKey
      : NO_OPENING;

    this.applyInitial(this.accessControl, map.isPublic ? 'public' : 'private');
    this.applyInitial(this.commentsControl, this.saved.comments as string);
    this.applyInitial(
      this.allowWebsiteEmbeddingControl,
      map.allowWebsiteEmbedding ?? false
    );
    this.applyInitial(
      this.linkViewModeControl,
      linkagePresetOf(map.smapletLinkViewMode ?? LinkViewMode.ALL).toString()
    );
    this.applyInitial(this.openingControl, openingKey);
    this.applyInitial(this.silentControl, map.smapletOpeningSilent ?? true);
    this.applyInitial(this.viewControl, this.saved.view as string);
    this.applyInitial(this.autoOrbitControl, this.saved.autoOrbit);
    this.buttonOrLinkNameControl.setValue(
      this.translate.instant('EMBED_DIALOG.DEFAULT_SMAP_LABEL', {
        name: this.data.mapName?.trim() || 'Unnamed',
      })
    );
    this.gatedControls().forEach((control) =>
      control.enable({ emitEvent: false })
    );
    this.onOpeningChange(openingKey);
    this.isInitialized = true;
  }

  /** Everything the load owns, and therefore must fill before anyone edits it. */
  private gatedControls(): FormControl[] {
    return [
      this.accessControl,
      this.allowWebsiteEmbeddingControl,
      this.commentsControl,
      this.linkViewModeControl,
      this.openingControl,
      this.silentControl,
      this.viewControl,
      this.autoOrbitControl,
    ] as FormControl[];
  }

  /** Never unlock Update on a failed load: its defaults are not the smap's settings. */
  private loadFailed(): void {
    this.isInitialized = false;
    this.snackBar.open(
      this.translate.instant('EMBED_DIALOG.LOAD_FAILED'),
      '',
      { duration: 4000 }
    );
  }

  /**
   * The load is a single response and Update stays disabled until it lands, so
   * there is no half-filled form to protect: every control takes the stored
   * value. Skipping a control that already looked touched left it on its
   * constructed default, which Update then wrote back over the smap (MAP-1682).
   */
  private applyInitial<T>(control: FormControl<T | null>, value: T): void {
    control.setValue(value, { emitEvent: false });
    control.markAsPristine();
  }

  private showVisibilityWarning(usage: 'embedding' | 'publishing' | 'publishing and embedding') {
    this.dialog.open(VisibilityWarningDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        mapName: this.data.map.name,
        usage
      }
    });
  }

  onAccessChange(option: {key: string, value: string, icon?: string}) {
    if (option.key === 'private') {
      // User selected private - check if we need to show warning
      if (this.initialAllowEmbedding || this.initialIsPublished) {
        let usage: 'embedding' | 'publishing' | 'publishing and embedding';
        if (this.initialIsPublished && this.initialAllowEmbedding) {
          usage = 'publishing and embedding';
        } else if (this.initialIsPublished) {
          usage = 'publishing';
        } else {
          usage = 'embedding';
        }
        this.showVisibilityWarning(usage);
      }
      this.allowWebsiteEmbeddingControl.setValue(false, { emitEvent: false });
    }
  }

  onEmbeddingToggleClick() {
    // If toggle is being turned on and visibility is private, switch to public
    if (!this.allowWebsiteEmbeddingControl.value && this.accessControl.value === 'private') {
      this.accessControl.setValue('public');
    }
    // If toggle is being turned off and it was initially on, show warning
    if (this.allowWebsiteEmbeddingControl.value && this.initialAllowEmbedding) {
      this.showDeactivatingWarning('embedding');
    }
  }

  private showDeactivatingWarning(deactivating: 'embedding' | 'publishing') {
    this.dialog.open(VisibilityWarningDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        mapName: this.data.map.name,
        usage: deactivating,
        deactivating
      }
    });
  }

  /** None, then the stories viewers can see, then every scene, each list alphabetized. */
  private buildOpeningOptions(tours: MapTour[], views: SavedView[]): void {
    const stories = tours.filter((tour) => !tour.excludeFromReadOnly).sort(byName);
    const scenes = [...views].sort(byName);
    this.hiddenSceneIds = new Set(views.filter((view) => view.excludeFromReadOnly).map((view) => view.id as string));
    this.openingOptions = [
      {
        key: NO_OPENING,
        value: this.translate.instant('EMBED_DIALOG.OPENING.NONE'),
        icon: 'icon-smap-action-reject',
        isSeparatorAfter: stories.length > 0 || scenes.length > 0,
      },
      ...stories.map((story, i) => ({
        key: openingKeyOf(story.id, null),
        value: story.name,
        icon: 'icon-smap-object-story',
        isSeparatorAfter: i === stories.length - 1 && scenes.length > 0,
      })),
      ...scenes.map((scene) => ({
        key: openingKeyOf(null, scene.id),
        value: scene.name,
        icon: 'icon-smap-object-scene',
      })),
    ];
  }

  openingIsScene(): boolean {
    return (this.openingControl.value ?? '').startsWith('scene:');
  }

  /** A scene hidden from viewers can only be presented silently. */
  onOpeningChange(key: string): void {
    const sceneId = key.startsWith('scene:') ? key.slice('scene:'.length) : null;
    if (sceneId && this.hiddenSceneIds.has(sceneId)) {
      this.silentControl.setValue(true, { emitEvent: false });
      this.silentControl.disable({ emitEvent: false });
    } else {
      this.silentControl.enable({ emitEvent: false });
    }
  }

  getDynamicLabelKey(): string {
    const option = this.embedOption.value;
    switch (option) {
      case 'button':
        return 'EMBED_DIALOG.STYLE.BUTTON_NAME';
      case 'link':
        return 'EMBED_DIALOG.STYLE.HYPERLINK_NAME';
      default:
        return 'EMBED_DIALOG.STYLE.BUTTON_LINK_NAME';
    }
  }

  private initializeOptions(): void {
    this.translate.get([
      'EMBED_DIALOG.STYLE.IFRAME',
      'EMBED_DIALOG.STYLE.BUTTON',
      'EMBED_DIALOG.STYLE.LINK',
      'EMBED_DIALOG.ACCESS.PUBLIC',
      'EMBED_DIALOG.ACCESS.PRIVATE',
      'LINKS.PRESET_SHOW_ALL',
      'LINKS.PRESET_SHOW_CONNECTED',
      'LINKS.PRESET_HIDE_ALL',
      'EMBED_DIALOG.VIEW.OPTION_2D',
      'EMBED_DIALOG.VIEW.OPTION_3D',
      'EMBED_DIALOG.VISITOR_COMMENTS_NONE',
      'EMBED_DIALOG.VISITOR_COMMENTS_MAPORIUM',
      'EMBED_DIALOG.VISITOR_COMMENTS_DISQUS'
    ]).subscribe(translations => {
      this.embedOptions = [
        { key: 'iframe', value: translations['EMBED_DIALOG.STYLE.IFRAME'], icon: 'integration_instructions' },
        { key: 'button', value: translations['EMBED_DIALOG.STYLE.BUTTON'], icon: 'smart_button' },
        { key: 'link', value: translations['EMBED_DIALOG.STYLE.LINK'], icon: 'link' }
      ];

      this.accessOptions = [
        { key: 'public', value: translations['EMBED_DIALOG.ACCESS.PUBLIC'], icon: 'public' },
        { key: 'private', value: translations['EMBED_DIALOG.ACCESS.PRIVATE'], icon: 'lock' }
      ];

      this.linkViewModeOptions = [
        { key: LinkViewMode.ALL.toString(), value: translations['LINKS.PRESET_SHOW_ALL'], icon: 'icon-smap-linkage-all' },
        { key: LinkViewMode.RELATED.toString(), value: translations['LINKS.PRESET_SHOW_CONNECTED'], icon: 'icon-smap-linkage-attached' },
        { key: LinkViewMode.OFF.toString(), value: translations['LINKS.PRESET_HIDE_ALL'], icon: 'icon-smap-linkage-none' }
      ];

      this.viewOptions = [
        { key: '2d', value: translations['EMBED_DIALOG.VIEW.OPTION_2D'], icon: 'crop_square' },
        { key: '3d', value: translations['EMBED_DIALOG.VIEW.OPTION_3D'], icon: 'deployed_code' }
      ];

      this.commentsOptions = [
        { key: 'none', value: translations['EMBED_DIALOG.VISITOR_COMMENTS_NONE'], icon: 'icon-smap-action-reject', isSeparatorAfter: true },
        { key: 'maporium', value: translations['EMBED_DIALOG.VISITOR_COMMENTS_MAPORIUM'], icon: 'icon-smap-object-comment' },
        { key: 'disqus', value: translations['EMBED_DIALOG.VISITOR_COMMENTS_DISQUS'], icon: 'icon-smap-provider-disqus' }
      ];

    });
  }

  iframeCode(mapId: string): string {
    return `<iframe sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      src="${this.smapletUrlFor(mapId)}"
      allow="autoplay"
      width="100%" height="100%">
    </iframe>`;
  }

  fullscreenButtonCode(mapId: string): string {
    const buttonLabel = this.buttonOrLinkNameControl.value?.trim() || this.translate.instant('EMBED_DIALOG.DEFAULT_SMAP_LABEL', { name: this.data.mapName || '' });
    const scriptCode = `<script src="${this.host}/assets/embeds/fullscreen-script.min.js"></script>`;

    const buttonCode = `${scriptCode}

    <button onclick="openFullscreenMap('${mapId}', '${this.data.mapName || ''}', '${this.host}')">${buttonLabel}</button> `;

    return buttonCode;
  }

  fullscreenLinkCode(mapId: string): string {
    const linkLabel = this.buttonOrLinkNameControl.value?.trim() || this.translate.instant('EMBED_DIALOG.DEFAULT_SMAP_LABEL', { name: this.data.mapName || '' });
    const scriptCode = `<script src="${this.host}/assets/embeds/fullscreen-script.min.js"></script>`;

    const linkCode = `${scriptCode}

    <a href="javascript:openFullscreenMap('${mapId}', '${this.data.mapName || ''}', '${this.host}')">${linkLabel}</a> `;

    return linkCode;
  }

  private smapletUrlFor(mapId: string): string {
    return `${this.host}${SMAPLET_PATH}?smapId=${encodeURIComponent(mapId)}`;
  }

  smapletUrl(): string {
    return this.smapletUrlFor(this.data.map.id);
  }

  embedSnippet(): string {
    const mapId = this.data.map.id;
    if (this.embedOption.value === 'iframe') return this.iframeCode(mapId);
    if (this.embedOption.value === 'button') return this.fullscreenButtonCode(mapId);
    return this.fullscreenLinkCode(mapId); // Default to link if selected
  }

  onCodeCopied() {
    this.snackBar.open(this.translate.instant('EMBED_DIALOG.CODE_COPIED'), '', { duration: 2000 });
  }

  onUrlCopied() {
    this.snackBar.open(this.translate.instant('EMBED_DIALOG.URL_COPIED'), '', { duration: 2000 });
  }

  done() {
    // The dialog's defaults are not the smap's settings: submitting them before
    // the load landed sent the map private and turned embedding off (MAP-1682).
    if (!this.isInitialized || !this.saved || this.saving) return;
    this.saving = true;

    const saved = this.saved;
    const mapId = this.data.map.id;
    const publish = this.accessControl.value === 'public';
    const allowEmbedding = this.allowWebsiteEmbeddingControl.value ?? false;
    // A select reaches its form control through a 200ms debounce, so a value
    // that has not caught up reads as null and must not be taken for a change.
    // the preset decides all three; older smaplets read them separately.
    const linkViewMode = this.chosen(
      this.linkViewModeControl,
      linkagePresetOf(saved.linkViewMode)
    ) as LinkViewMode;
    const {otherLinksMode, otherNodesMode} = LINKAGE_PRESET_MODES[linkagePresetOf(linkViewMode)];

    // Only what actually changed is sent. Writing all five every time let one
    // stale control revert a setting the user never touched (MAP-1682), and
    // publish has to lead: the API refuses embedding on a private smap.
    const writes: Observable<unknown>[] = [];
    if (publish !== saved.isPublic) {
      writes.push(publish ? this.publish() : this.unpublish());
    }
    if (linkViewMode !== saved.linkViewMode) {
      writes.push(this.mapService.setLinkViewMode(mapId, linkViewMode));
    }
    if (otherLinksMode !== saved.otherLinksMode) {
      writes.push(this.mapService.setOtherLinksMode(mapId, otherLinksMode));
    }
    if (otherNodesMode !== saved.otherNodesMode) {
      writes.push(this.mapService.setOtherNodesMode(mapId, otherNodesMode));
    }
    if (allowEmbedding !== saved.allowEmbedding) {
      writes.push(this.mapService.setAllowWebsiteEmbedding(mapId, allowEmbedding));
    }
    const comments = isSmapletComments(this.commentsControl.value)
      ? this.commentsControl.value
      : saved.comments;
    if (comments !== saved.comments) {
      writes.push(this.mapService.setSmapletComments(mapId, comments));
    }
    const openingKey = this.openingControl.value || saved.openingKey;
    const openingStoryId = openingKey.startsWith('story:') ? openingKey.slice('story:'.length) : null;
    const openingSceneId = openingKey.startsWith('scene:') ? openingKey.slice('scene:'.length) : null;
    const openingSilent = this.silentControl.disabled ? true : (this.silentControl.value ?? true);
    if (openingKey !== saved.openingKey || openingSilent !== saved.openingSilent) {
      writes.push(this.mapService.setSmapletOpening(mapId, {
        storyId: openingStoryId,
        sceneId: openingSceneId,
        silent: openingSilent,
      }));
    }
    const pickedView = this.viewControl.value === '3d' || this.viewControl.value === '2d'
      ? this.viewControl.value
      : saved.view;
    const view = this.smaplet3dAllowed ? pickedView : saved.view;
    if (view !== saved.view) {
      writes.push(this.mapService.setSmapletView(mapId, view));
    }
    const autoOrbit = this.smaplet3dAllowed && view === '3d'
      ? (this.autoOrbitControl.value ?? saved.autoOrbit)
      : saved.autoOrbit;
    if (autoOrbit !== saved.autoOrbit) {
      writes.push(this.mapService.setSmapletAutoOrbit(mapId, autoOrbit));
    }
    // Going private takes the smap out of the store with it.
    if (!publish && this.initialIsPublished) {
      writes.push(this.storeService.unpublishMap(mapId));
    }

    (writes.length > 0 ? concat(...writes) : of(null)).subscribe({
      complete: () => {
        this.data.map.isPublic = publish;
        this.data.map.smapletLinkViewMode = linkViewMode;
        this.data.map.smapletOtherLinksMode = otherLinksMode;
        this.data.map.smapletOtherNodesMode = otherNodesMode;
        this.data.map.allowWebsiteEmbedding = allowEmbedding;
        this.data.map.hasGuestComments = comments === 'maporium';
        this.data.map.smapletComments = comments;
        this.data.map.smapletOpeningStoryId = openingStoryId;
        this.data.map.smapletOpeningSceneId = openingSceneId;
        this.data.map.smapletOpeningSilent = openingSilent;
        this.data.map.smapletView = view;
        this.data.map.smapletAutoOrbit = autoOrbit;
        this.mapService.setCurrentSelectedMapToStore(this.data.map);
        this.saving = false;
        this.matDialogRef.close({ published: publish });
      },
      // A call part way down used to abort the rest in silence, leaving the
      // smap with only some of what the dialog was asked to save.
      error: () => {
        this.saving = false;
        this.snackBar.open(
          this.translate.instant('EMBED_DIALOG.SAVE_FAILED'),
          '',
          { duration: 4000 }
        );
      },
    });
  }

  /** A select's value as a number, or the stored one when it has none yet. */
  private chosen(control: FormControl, fallback: number): number {
    const value = Number(control.value);
    return Number.isFinite(value) && control.value !== null && control.value !== ''
      ? value
      : fallback;
  }

  publish() {
    return this.mapService.openPublicAccessToMap(this.data.map.id);
  }

  unpublish() {
    return this.mapService.closePublicAccessToMap(this.data.map.id);
  }

  protected readonly packagenames = PACKAGE_NAMES;
}


